import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { message } from "antd";
import { useGetBalanceQuery } from "../../../store/service/clientBetService";
import {
  useGetMyBankAccountsQuery,
  useSubmitWithdrawalMutation,
} from "../../../store/service/paymentService";
import ClientPaymentRequestTables from "./ClientPaymentRequestTables";
import "./Client.scss";

const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const MIN_WITHDRAW = 100;

const ClientWithdraw = () => {
  const [amount, setAmount] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [accountName, setAccountName] = useState("");
  const [selectedBankId, setSelectedBankId] = useState("");
  const [agree, setAgree] = useState(false);
  const [touched, setTouched] = useState({});
  const [submitWithdrawal, { isLoading }] = useSubmitWithdrawalMutation();
  const { data: banksData } = useGetMyBankAccountsQuery();
  const { data: balanceData } = useGetBalanceQuery(undefined, { pollingInterval: 10000 });
  const banks = banksData?.data || [];
  const balance = Number(balanceData?.data?.balance ?? 0);

  useEffect(() => {
    if (!banks.length || selectedBankId) return;
    const def = banks.find((b) => b.isDefault) || banks[0];
    if (!def) return;
    setSelectedBankId(String(def.id));
    setAccountNumber(def.accountNumber || "");
    setIfsc(def.ifscCode || "");
    setAccountName(def.accountName || "");
  }, [banks, selectedBankId]);

  const onPickBank = (id) => {
    setSelectedBankId(id);
    if (!id) {
      setAccountNumber("");
      setIfsc("");
      setAccountName("");
      return;
    }
    const bank = banks.find((b) => String(b.id) === String(id));
    if (!bank) return;
    setAccountNumber(bank.accountNumber || "");
    setIfsc(bank.ifscCode || "");
    setAccountName(bank.accountName || "");
  };

  const mark = (field) => setTouched((t) => ({ ...t, [field]: true }));

  const errors = useMemo(() => {
    const e = {};
    const amt = Number(amount);
    if (!amount || Number.isNaN(amt)) {
      e.amount = "Enter withdrawal amount";
    } else if (amt < MIN_WITHDRAW) {
      e.amount = `Minimum withdrawal is ${MIN_WITHDRAW}`;
    } else if (amt > balance) {
      e.amount = `Cannot withdraw more than available balance (${balance.toFixed(2)})`;
    }

    const acc = String(accountNumber || "").trim();
    if (!acc) e.accountNumber = "Account number is required";
    else if (!/^\d{9,18}$/.test(acc)) e.accountNumber = "Enter a valid account number (9–18 digits)";

    const code = String(ifsc || "").trim().toUpperCase();
    if (!code) e.ifsc = "IFSC code is required";
    else if (!IFSC_RE.test(code)) e.ifsc = "Enter a valid IFSC (e.g. SBIN0001234)";

    const name = String(accountName || "").trim();
    if (!name) e.accountName = "Account holder name is required";
    else if (name.length < 2) e.accountName = "Enter the full account holder name";

    if (!agree) e.agree = "Please confirm your bank details";

    return e;
  }, [amount, balance, accountNumber, ifsc, accountName, agree]);

  const show = (field) => touched[field] && errors[field];

  const onSubmit = async (e) => {
    e.preventDefault();
    setTouched({
      amount: true,
      accountNumber: true,
      ifsc: true,
      accountName: true,
      agree: true,
    });
    if (Object.keys(errors).length) {
      message.error(Object.values(errors)[0]);
      return;
    }

    try {
      const res = await submitWithdrawal({
        amount: Number(amount),
        account_number: accountNumber.trim(),
        ifsc: ifsc.trim().toUpperCase(),
        account_name: accountName.trim(),
      }).unwrap();
      message.success(res.message || "Withdrawal request submitted");
      setAmount("");
      setAgree(false);
      setTouched({});
    } catch (err) {
      message.error(err?.data?.message || "Submit failed");
    }
  };

  return (
    <div className="lotus-pay-page">
      <Link to="/play" className="lotus-back-btn">
        ← Back
      </Link>

      <div className="lotus-section-title">Withdraw</div>

      <div className="lotus-bank-card">
        <h3>Withdrawal request</h3>
        <p className="lotus-withdraw-hint">
          Available balance: <strong>{balance.toFixed(2)}</strong>. You cannot withdraw more than
          this.
        </p>
      </div>

      <form className="lotus-pay-form" onSubmit={onSubmit} noValidate>
        <label className={show("amount") ? "has-error" : ""}>
          Amount *
          <input
            type="number"
            placeholder={`Min ${MIN_WITHDRAW} · Max ${balance.toFixed(2)}`}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onBlur={() => mark("amount")}
            min={MIN_WITHDRAW}
            max={balance}
            step="0.01"
            required
          />
          {show("amount") ? <span className="field-error">{errors.amount}</span> : null}
        </label>

        {banks.length > 0 ? (
          <label>
            Saved bank account
            <select value={selectedBankId} onChange={(e) => onPickBank(e.target.value)}>
              {banks.map((bank) => (
                <option key={bank.id} value={bank.id}>
                  {bank.accountName} · {bank.accountNumber}
                  {bank.isDefault ? " (default)" : ""}
                </option>
              ))}
              <option value="">Enter manually</option>
            </select>
          </label>
        ) : (
          <p className="lotus-withdraw-hint">
            No saved bank yet. You can add one from{" "}
            <Link to="/play/profile">Profile</Link>, or enter details below.
          </p>
        )}

        <label className={show("accountNumber") ? "has-error" : ""}>
          Account number *
          <input
            type="text"
            inputMode="numeric"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 18))}
            onBlur={() => mark("accountNumber")}
            required
          />
          {show("accountNumber") ? (
            <span className="field-error">{errors.accountNumber}</span>
          ) : null}
        </label>

        <label className={show("ifsc") ? "has-error" : ""}>
          IFSC code *
          <input
            type="text"
            value={ifsc}
            onChange={(e) => setIfsc(e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 11))}
            onBlur={() => mark("ifsc")}
            maxLength={11}
            required
          />
          {show("ifsc") ? <span className="field-error">{errors.ifsc}</span> : null}
        </label>

        <label className={show("accountName") ? "has-error" : ""}>
          Account holder name *
          <input
            type="text"
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
            onBlur={() => mark("accountName")}
            required
          />
          {show("accountName") ? <span className="field-error">{errors.accountName}</span> : null}
        </label>

        <label className={`lotus-pay-check ${show("agree") ? "has-error" : ""}`}>
          <input
            type="checkbox"
            checked={agree}
            onChange={(e) => {
              setAgree(e.target.checked);
              mark("agree");
            }}
          />
          <span>
            I confirm these bank details are correct.
            {show("agree") ? <span className="field-error block">{errors.agree}</span> : null}
          </span>
        </label>

        <button
          type="submit"
          className="lotus-pay-submit"
          disabled={isLoading || Object.keys(errors).length > 0}>
          {isLoading ? "Submitting…" : "Submit withdrawal"}
        </button>
      </form>

      <ClientPaymentRequestTables filter="all" />
    </div>
  );
};

export default ClientWithdraw;
